import { cn } from '@/lib/utils';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { Transaction } from '@tiptap/pm/state';
import {
  BubbleMenu,
  EditorProvider,
  useCurrentEditor,
  type Editor,
  type JSONContent,
} from '@tiptap/react';
import { ExternalLink, Pencil, Unlink } from 'lucide-react';
import { useCallback, useEffect, useId, useImperativeHandle, useRef } from 'react';
import { Icons } from '../icons';
import { ScrollArea, ScrollBar } from '../ui/scroll-area';
import EditorIcon from './EditorIcon';
import { FieldErrors } from './FieldErrors';

type EditorStuffProps = {
  ref: any;
};

const EditorStuff = ({ ref }: EditorStuffProps) => {
  const { editor } = useCurrentEditor();

  const editorRef: React.MutableRefObject<Editor | null> = useRef(null);

  useImperativeHandle(ref, () => ({
    clearContent: () => {
      editorRef.current?.commands.clearContent();
    },
    focus: () => {
      editorRef.current?.commands.focus();
    },
    setContent: (content: string) => {
      editorRef.current?.commands.setContent(content);
    },
    getContent: () => {
      return editorRef.current?.getHTML();
    },
  }));
  if (!editor) return null;
  editorRef.current = editor;

  return null;
};

const EditableUpdater = ({ disabled }: { disabled: boolean }) => {
  const { editor } = useCurrentEditor();

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  return null;
};

const LinkBubbleMenu = () => {
  const { editor } = useCurrentEditor();

  const handleOpenLink = useCallback(() => {
    if (!editor) return;
    const href = editor.getAttributes('link').href;

    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }, [editor]);

  const handleEditLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    let url = window.prompt('Enter a URL to link to', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }, [editor]);

  if (!editor) return null;

  const href = editor.getAttributes('link').href;

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 100, placement: 'bottom-start' }}
      shouldShow={({ editor: ed }) => ed.isActive('link')}
    >
      <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-lg dark:border-dark-gray-600 dark:bg-dark-gray-800">
        <span
          className="max-w-[200px] truncate px-2 text-sm text-gray-600 dark:text-dark-gray-300"
          title={href}
        >
          {href}
        </span>
        <button
          type="button"
          onClick={handleOpenLink}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-gray-400 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-200"
          title="Open link"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={handleEditLink}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-gray-400 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-200"
          title="Edit link"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={handleRemoveLink}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:text-dark-gray-400 dark:hover:bg-dark-gray-700 dark:hover:text-red-400"
          title="Remove link"
        >
          <Unlink className="h-4 w-4" />
        </button>
      </div>
    </BubbleMenu>
  );
};

type MenuBarProps = {
  extraButtons?: {
    icon: Icons;
    type: 'setContent';
    fn: () => any;
  }[];
  ref: any;
  className?: string;
};

const MenuBar = ({ ref, extraButtons, className }: MenuBarProps) => {
  const { editor } = useCurrentEditor();

  const setLink = useCallback(() => {
    if (!editor) return;
    const isLinked = editor.isActive('link');

    if (isLinked) {
      editor.chain().focus().unsetLink().run();
    } else {
      const previousUrl = editor.getAttributes('link').href;
      let url = window.prompt('Enter a URL to link to', previousUrl);

      if (url === null) {
        return;
      }

      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();

        return;
      }

      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }

      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const clearFormatting = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus('all')
      .selectAll()
      .clearNodes()
      .unsetBold()
      .unsetItalic()
      .unsetStrike()
      .focus(1)
      .run();
  }, [editor]);

  if (!editor) return null;

  return (
    <ScrollArea>
      <div
        className={cn(
          'flex flex-row flex-nowrap gap-1 border border-b-0 min-w-full border-gray-300 rounded-t-md transition duration-150 ease-in-out sm:leading-5 dark:bg-dark-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200 outline-none p-2 h-12 bg-gray-50 justify-between',
          className
        )}
      >
        <div>
          <EditorIcon
            icon={Icons.bold}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
          />
          <EditorIcon
            icon={Icons.italic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
          />
          <EditorIcon
            icon={Icons.strikethrough}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
          />
          <EditorIcon icon={Icons.link} isActive={editor.isActive('link')} onClick={setLink} />
          <EditorIcon
            icon={Icons.unorderedList}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
          />
          <EditorIcon
            icon={Icons.orderedList}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
          />
          <EditorIcon
            icon={Icons.blockQuote}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
          />
          <EditorIcon
            icon={Icons.columnsHorizontal}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          />
          <EditorIcon
            icon={Icons.undo}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
          />
          <EditorIcon
            icon={Icons.redo}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
          />
          <EditorIcon icon={Icons.eraser} onClick={clearFormatting} />
        </div>
        <div>
          {extraButtons?.map((button, index) => (
            <EditorIcon
              key={`editor-btn-${button.icon}-${index}`}
              icon={button.icon}
              onClick={async () => {
                const res = await button.fn?.();

                if (button.type === 'setContent') {
                  editor.commands.setContent(res || '');
                }
              }}
            />
          ))}
        </div>
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};

type Props = {
  ref?: any;
  value?: JSONContent | string;
  name?: string;
  label?: string;
  onChange?: (val?: any) => any;
  className?: string;
  containerClassName?: string;
  small?: boolean;
  toolbar?: boolean;
  html?: boolean;
  text?: boolean;
  disabled?: boolean;
  extraButtons?: {
    icon: Icons;
    type: 'setContent';
    fn: () => any;
  }[];
  errors?: FormError;
  labelColor?: boolean;
  extensions: any[];
  rightSide?: React.ReactNode;
};

export const TiptapEditorBase = ({
  ref,
  value,
  name,
  label,
  className = 'border block w-full border-gray-300 rounded-b-md transition duration-150 ease-in-out sm:leading-5 dark:bg-dark-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200 outline-none p-2',
  containerClassName,
  onChange,
  small,
  toolbar = true,
  html = false,
  text = false,
  disabled = false,
  extraButtons,
  errors,
  labelColor = false,
  extensions = [],
  rightSide,
}: Props) => {
  const id = useId();

  const debouncedOnUpdate = ({
    editor,
    transaction,
  }: {
    editor: any;
    transaction: Transaction;
  }) => {
    // Skip non-content events (e.g. setEditable) that fire onUpdate
    // without actually changing the document.
    if (!transaction.docChanged) return;
    const val = text ? editor.getText() : html ? editor.getHTML() : editor.getJSON();
    onChange?.(val);
  };

  return (
    <div
      className={cn(
        'tiptap prose flex flex-col w-full max-w-none! bg-white dark:bg-dark-gray-900',
        small ? 'tiptap-small' : '',
        containerClassName ?? ''
      )}
    >
      <div className={cn('flex flex-row justify-between items-bottom', rightSide ? 'pb-2' : '')}>
        {label && (
          <label
            className={cn(
              `block text-sm font-medium leading-5 sm:mt-px`,
              labelColor
                ? 'text-primary-600 dark:text-dark-gray-200'
                : 'text-gray-900  dark:text-dark-gray-200',
              rightSide ? 'sm:pt-4' : 'sm:pt-2'
            )}
            htmlFor={name || id}
          >
            {label}
          </label>
        )}
        {rightSide && (
          <div className={cn(!!disabled && 'opacity-50', 'flex flex-row justify-end')}>
            {rightSide}
          </div>
        )}
      </div>
      <EditorProvider
        content={value}
        extensions={extensions}
        onUpdate={debouncedOnUpdate}
        editable={!disabled}
        editorProps={{
          attributes: {
            class: cn(
              className,
              toolbar ? '' : 'rounded-t-md',
              disabled
                ? 'opacity-50 bg-gray-100 dark:bg-dark-gray-700 pointer-events-none'
                : 'pointer-events-auto'
            ),
            disabled: `${disabled}`,
            name: name || id,
          },
        }}
        slotBefore={
          toolbar ? (
            <MenuBar
              extraButtons={extraButtons}
              ref={ref}
              className={disabled ? 'opacity-50' : undefined}
            />
          ) : null
        }
      >
        <EditorStuff ref={ref} />
        <EditableUpdater disabled={disabled} />
        <LinkBubbleMenu />
      </EditorProvider>
      <FieldErrors errors={errors} />
    </div>
  );
};
